

import { useState, useEffect, useCallback } from 'react';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';

/* ---------------------------------------------------------------------- */
/*  Helpers                                                                 */
/* ---------------------------------------------------------------------- */

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};




const getTodayLabel = () =>
  new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const fmtINR = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN');

/* ---------------------------------------------------------------------- */
/*  Small presentational building blocks                                   */
/* ---------------------------------------------------------------------- */

function KpiCard({ label, value, delta, icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-left bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:border-purple-200 transition-all w-full"
    >
      <div className="flex items-center justify-between">
        <span className="text-2xl">{icon}</span>
        {delta !== undefined && delta !== null && (
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              delta >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
            }`}
          >
            {delta >= 0 ? '+' : ''}
            {delta}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-[#0D1B3D] mt-3">{value ?? '—'}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </button>
  );
}

function SectionCard({ title, action, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${className}`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-[#0D1B3D] text-sm">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    Pending: 'bg-amber-50 text-amber-600',
    Completed: 'bg-green-50 text-green-600',
    Scheduled: 'bg-blue-50 text-blue-600',
    Rescheduled: 'bg-purple-50 text-purple-600',
    Cancelled: 'bg-red-50 text-red-600',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${map[status] || 'bg-gray-50 text-gray-600'}`}>
      {status}
    </span>
  );
}

/* ---------------------------------------------------------------------- */
/*  Main component                                                          */
/* ---------------------------------------------------------------------- */

const PIPELINE_STAGES = [
  'New',
  'Contacted',
  'Interested',
  'Resume Received',
  'Employer Shared',
  'Interview Scheduled',
  'Interview Completed',
  'Offer Released',
  'Offer Accepted',
  'Joined',
];

// The `candidates.stage` column stores these lowercase snake_case values —
// this is what the filter dropdown must send to the API. Update this list
// if your app actually writes different strings when advancing a stage.
const STAGE_FILTER_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'resume_received', label: 'Resume Received' },
  { value: 'employer_shared', label: 'Employer Shared' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'interview_completed', label: 'Interview Completed' },
  { value: 'offer_released', label: 'Offer Released' },
  { value: 'offer_accepted', label: 'Offer Accepted' },
  { value: 'joined', label: 'Joined' },
];

function AgentDashboard() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [data, setData] = useState(null);

  // --- Attendance gating ---
  const [attendanceStatus, setAttendanceStatus] = useState('loading');
  const [breakType, setBreakType] = useState(null);

  // Step 18 - filters
    const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    city: '',
    employer_id: '',
    candidate_status: '',
    job_posting_id: '',
  });


    // Debounced copy of filters — this is what actually gets sent to the API
  const [appliedFilters, setAppliedFilters] = useState(filters);

  // Wait 400ms after the agent stops typing before applying the filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters(filters);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters]);


  // Step 19 - global search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

    // Employers this agent can filter by — dropdown, so we always send a real ID
  const [employerOptions, setEmployerOptions] = useState([]);


  const firstName = user?.name ? user.name.split(' ')[0] : 'Agent';

  const isNotCheckedIn = attendanceStatus === 'not_checked_in';
  const isOnBreak = attendanceStatus === 'on_break';
  const isCheckedIn = attendanceStatus === 'checked_in';
  const isCheckedOut = attendanceStatus === 'checked_out';

  const fetchAttendanceStatus = async () => {
    try {
      const res = await api.post('/agent/attendance/status');
      setAttendanceStatus(res.data.status);
      setBreakType(res.data.break_type || null);
    } catch (err) {
      console.error('Failed to fetch attendance status', err);
      setAttendanceStatus('not_checked_in');
    }
  };

  useEffect(() => {
    fetchAttendanceStatus();
    const interval = setInterval(fetchAttendanceStatus, 15000);
    const handleFocus = () => fetchAttendanceStatus();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadDashboard = useCallback(async () => {
    setStatus('loading');
    try {
      const params = Object.fromEntries(
        Object.entries(appliedFilters).filter(([, v]) => v !== '' && v !== null)
      );
      const res = await api.get('/agent/dashboard', { params });
      setData(res.data);
      setStatus('ok');
    } catch (err) {
      console.error('Agent dashboard load failed', err);
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  // Only pull the full dashboard payload once the agent is actually checked in —
  // no point hitting the filtered dashboard endpoint while they're locked out by attendance.
  useEffect(() => {
    if (isCheckedIn) {
      loadDashboard();
    }
  }, [isCheckedIn, loadDashboard]);

  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/agent/search', { params: { q: term } });
        setSearchResults(res.data);
      } catch (err) {
        console.error('Global search failed', err);
        setSearchResults({ error: true });
      } finally {
        setSearching(false);
      }
    }, 350); // debounce
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

    // Applies immediately — used by the Apply button and Enter key,
  // bypassing the 400ms debounce so results update the instant you ask.
  const applyFiltersNow = (e) => {
    e?.preventDefault();
    setAppliedFilters(filters);
  };

    const clearFilters = () => {
    const empty = {
      date_from: '', date_to: '', city: '', employer_id: '',
      candidate_status: '', job_posting_id: '',
    };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  // Safe pull-outs with sensible fallbacks so the page never crashes
  // while the backend is still being wired up.
  const kpis = data?.kpis || {};
  const pipeline = data?.pipeline || {};
  const followUps = data?.follow_ups || [];
  const interviews = data?.interviews || [];
  const jobs = data?.jobs || [];
  const employers = data?.employers || [];

    useEffect(() => {
    if (data?.employers) {
      setEmployerOptions(data.employers);
    }
  }, [data]);
  // const subscriptions = data?.subscriptions || [];
  const performance = data?.performance || {};
  const target = data?.target || { total: 0, completed: 0 };
  const commission = data?.commission || {};
  const notifications = data?.notifications || [];
  const activities = data?.activities || [];
  const weeklyTrend = data?.weekly_trend || []; // [{label:'Mon', count:5}, ...]

  const targetPct = target.total ? Math.round((target.completed / target.total) * 100) : 0;
  const maxTrend = Math.max(1, ...weeklyTrend.map((d) => d.count || 0));

  return (
 
  <AgentLayout>
    <div className="-m-4 sm:-m-6 p-4 sm:p-6 min-h-[calc(100vh-57px)] bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      {/* ---------------- Welcome (always visible) ---------------- */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-4">
        <div>
<h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
            {getGreeting()} {firstName} 👋
          </h2>
          <p className="text-gray-500 text-sm mt-1">{getTodayLabel()}</p>
          <p className="text-gray-500 text-sm">Have a productive day.</p>
        </div>

        {/* Global search only makes sense once checked in */}
        {isCheckedIn && (
          <div className="relative w-full md:w-80">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search candidates, employers, jobs, mobile, email…"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
            {searchTerm.trim().length >= 2 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-100 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                {searching && <p className="p-3 text-xs text-gray-400">Searching…</p>}
                {!searching && searchResults?.error && (
                  <p className="p-3 text-xs text-red-500">Search failed. Try again.</p>
                )}
                {!searching &&
                  searchResults &&
                  !searchResults.error &&
                  ['candidates', 'employers', 'jobs'].map((group) =>
                    (searchResults[group] || []).length ? (
                      <div key={group} className="border-b border-gray-50 last:border-0">
                        <p className="px-3 pt-2 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                          {group}
                        </p>
                        {searchResults[group].map((item) => (
                          <a
                            key={`${group}-${item.id}`}
                            href={item.url || '#'}
                            className="block px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 text-gray-700"
                          >
                            {item.name}
                            {item.subtitle && (
                              <span className="text-gray-400 text-xs ml-2">{item.subtitle}</span>
                            )}
                          </a>
                        ))}
                      </div>
                    ) : null
                  )}
                {!searching &&
                  searchResults &&
                  !searchResults.error &&
                  !['candidates', 'employers', 'jobs'].some((g) => (searchResults[g] || []).length) && (
                    <p className="p-3 text-xs text-gray-400">No matches found.</p>
                  )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---------------- Attendance gating ---------------- */}
      {attendanceStatus === 'loading' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <p className="text-gray-500 text-sm">Checking attendance status…</p>
        </div>
      )}

      {isNotCheckedIn && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center">
          <p className="text-lg font-semibold text-[#0D1B3D] mb-1">You haven't checked in yet</p>
          <p className="text-sm text-gray-500">Please check in using the button at the top right to start your day.</p>
        </div>
      )}

      {isOnBreak && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl shadow p-6 text-center">
          <p className="text-lg font-semibold text-amber-700 mb-1">
            You're on {breakType || 'a break'} ☕
          </p>
          <p className="text-sm text-amber-600">Use the button at the top right to end your break when you're ready.</p>
        </div>
      )}

      {isCheckedOut && (
        <div className="bg-purple-50 border border-purple-100 rounded-2xl shadow-sm p-6 text-center">
          <p className="text-lg font-semibold text-purple-700 mb-1">Day complete — see you tomorrow! 👋</p>
          <p className="text-sm text-purple-600">Your attendance for today has been recorded.</p>
        </div>
      )}

      {/* ---------------- Full dashboard (only once checked in) ---------------- */}
      {isCheckedIn && (
        <>
          {status === 'error' && (
            <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl p-4 mb-6">
              Could not load the dashboard. Make sure you're logged in as an agent and the API is
              reachable.{' '}
              <button onClick={loadDashboard} className="underline font-medium">
                Retry
              </button>
            </div>
          )}

          {/* Filters */}
          <SectionCard
            title="Filters"
            action={
              <button onClick={clearFilters} className="text-xs font-semibold text-purple-600 hover:underline">
                Clear Filters
              </button>
            }
            className="mb-6"
          >
            <form onSubmit={applyFiltersNow} className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">

                <div className="flex flex-col">
                  <label className="text-[10px] uppercase text-gray-400 mb-1">From Date</label>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => updateFilter('date_from', e.target.value)}
                    className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-[10px] uppercase text-gray-400 mb-1">To Date</label>
                  <input
                    type="date"
                    value={filters.date_to}
                    min={filters.date_from || undefined}
                    onChange={(e) => updateFilter('date_to', e.target.value)}
                    className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                  />
                </div>

                <input
                  type="text"
                  placeholder="City"
                  value={filters.city}
                  onChange={(e) => updateFilter('city', e.target.value)}
                  className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent self-end"
                />
                <select
                  value={filters.employer_id}
                  onChange={(e) => updateFilter('employer_id', e.target.value)}
                  className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent self-end"
                >
                  <option value="">Employer</option>
                  {employerOptions.map((e) => (
                    <option key={e.id} value={e.id}>{e.company_name}</option>
                  ))}
                </select>

                <select
                  value={filters.candidate_status}
                  onChange={(e) => updateFilter('candidate_status', e.target.value)}
                  className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent self-end"
                >
                  <option value="">Candidate Status</option>
                  {STAGE_FILTER_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Job (posting ID)"
                  value={filters.job_posting_id}
                  onChange={(e) => updateFilter('job_posting_id', e.target.value)}
                  className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent self-end"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white text-sm font-semibold px-6 py-2 rounded-full shadow-sm transition"
                >
                  🔍 Apply Filters
                </button>
              </div>
            </form>
          </SectionCard>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard icon="🧑‍💼" label="Today's Candidates" value={kpis.today_candidates} delta={kpis.today_candidates_delta} />
            <KpiCard icon="👥" label="Total Candidates" value={kpis.total_candidates} />
            <KpiCard icon="📞" label="Today's Follow-ups" value={kpis.today_follow_ups} />
            <KpiCard icon="⏳" label="Pending Follow-ups" value={kpis.pending_follow_ups} />
            <KpiCard icon="🗓️" label="Today's Interviews" value={kpis.today_interviews} />
            <KpiCard icon="🏆" label="Placements" value={kpis.placements} delta={kpis.placements_delta} />
            <KpiCard icon="💰" label="Commission" value={fmtINR(kpis.commission)} />
            <KpiCard icon="🔔" label="Notifications" value={kpis.notifications} />
          </div>

        {/* Quick Actions */}
<div className="flex flex-wrap gap-3 mb-6">
 
{[
  { label: '+ Add Candidate', href: '/agent/candidates?new=1' },
  { label: '+ Add Employer', href: '/agent/employers?new=1' },
  { label: '+ Schedule Interview', href: '/agent/interviews?new=1' },
  { label: '+ Create Follow-up', href: '/agent/follow-ups?new=1' },
  // { label: '+ Upload Resume', href: '/agent/candidates?upload=1' },
].map((a) => (
  <a
    key={a.label}
  href={a.href}
  className="bg-white text-purple-600 border border-gray-100 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white text-sm font-semibold px-4 py-2.5 rounded-full transition shadow-sm"
>
  {a.label}
</a>
))}
</div>

          {/* Candidate Pipeline */}

          {/* Candidate Pipeline */}
          <SectionCard title="Candidate Pipeline" className="mb-6">
            <div className="flex overflow-x-auto gap-3 pb-1">
              {PIPELINE_STAGES.map((stage) => (
                <a
                  key={stage}
                  href={`/agent/candidates?stage=${encodeURIComponent(stage)}`}
                  className="min-w-[130px] flex-shrink-0 bg-purple-50/60 hover:bg-purple-100 rounded-xl p-3 text-center transition-colors"
                >
                  <p className="text-xl font-bold text-[#0D1B3D]">{pipeline[stage] ?? 0}</p>
                  <p className="text-[11px] text-gray-500 mt-1 leading-tight">{stage}</p>
                </a>
              ))}
            </div>
          </SectionCard>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            {/* Today's Follow-ups */}
            <SectionCard title="Today's Follow-ups" className="xl:col-span-2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100">
                      <th className="pb-2 font-medium">Candidate</th>
                      <th className="pb-2 font-medium">Mobile</th>
                      <th className="pb-2 font-medium">Time</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followUps.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-4 text-center text-gray-400 text-sm">
                          No follow-ups scheduled for today.
                        </td>
                      </tr>
                    )}
                    {followUps.map((f) => (
                      <tr key={f.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                        <td className="py-2.5">{f.candidate_name}</td>
                        <td className="py-2.5">{f.mobile}</td>
                        <td className="py-2.5">{f.time}</td>
                        <td className="py-2.5">
                          <StatusPill status={f.status} />
                        </td>
                        <td className="py-2.5 text-right space-x-2 whitespace-nowrap">
                          <a href={`tel:${f.mobile}`} className="text-blue-600 hover:underline">Call</a>
                          <a href={`https://wa.me/91${f.mobile}`} target="_blank" rel="noreferrer" className="text-green-600 hover:underline">WhatsApp</a>
                          <button className="text-gray-500 hover:underline">Notes</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* Performance Widget */}
            <SectionCard title="Performance Today">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-2xl font-bold text-[#0D1B3D]">{performance.calls_today ?? 0}</p>
                  <p className="text-xs text-gray-500">Calls Today</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0D1B3D]">{performance.candidates_added ?? 0}</p>
                  <p className="text-xs text-gray-500">Candidates Added</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0D1B3D]">{performance.placements ?? 0}</p>
                  <p className="text-xs text-gray-500">Placements</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0D1B3D]">{performance.conversion ?? 0}%</p>
                  <p className="text-xs text-gray-500">Conversion</p>
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            {/* Upcoming Interviews */}
            <SectionCard title="Upcoming Interviews" className="xl:col-span-2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100">
                      <th className="pb-2 font-medium">Candidate</th>
                      <th className="pb-2 font-medium">Company</th>
                      <th className="pb-2 font-medium">Type</th>
                      <th className="pb-2 font-medium">Date &amp; Time</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interviews.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-gray-400 text-sm">
                          No interviews scheduled.
                        </td>
                      </tr>
                    )}
                    {interviews.map((i) => (
                      <tr key={i.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                        <td className="py-2.5">{i.candidate_name}</td>
                        <td className="py-2.5">{i.company_name}</td>
                        <td className="py-2.5">{i.type}</td>
                        <td className="py-2.5">{i.date} · {i.time}</td>
                        <td className="py-2.5">
                          <StatusPill status={i.status} />
                        </td>
                        <td className="py-2.5 text-right space-x-2 whitespace-nowrap">
                          <a href={`/agent/interviews/${i.id}`} className="text-purple-600 hover:underline">Open</a>
                          <button className="text-amber-600 hover:underline">Reschedule</button>
                          <button className="text-gray-500 hover:underline">Feedback</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* Target Widget */}
            <SectionCard title="Monthly Target">
              <p className="text-sm text-gray-500 mb-1">Candidates</p>
              <p className="text-3xl font-bold text-[#0D1B3D] mb-3">{target.total ?? 0}</p>
              <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full bg-gradient-to-r from-[#7C3AED] to-[#DB2777] rounded-full transition-all"
                  style={{ width: `${Math.min(100, targetPct)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>Completed: {target.completed ?? 0}</span>
                <span>Remaining: {Math.max(0, (target.total ?? 0) - (target.completed ?? 0))}</span>
              </div>
              <p className="text-right text-xs font-semibold text-purple-600 mt-1">{targetPct}%</p>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Assigned Jobs */}
            <SectionCard title="Assigned Jobs" className="lg:col-span-2">
              <div className="grid sm:grid-cols-2 gap-3">
                {jobs.length === 0 && <p className="text-sm text-gray-400 col-span-2 text-center py-4">No jobs assigned yet.</p>}
                {jobs.map((j) => (
                  <div key={j.id} className="border border-gray-100 rounded-xl p-4 hover:border-purple-200 transition-colors">
                    <p className="font-semibold text-[#0D1B3D] text-sm">{j.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{j.company_name}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-2">
                      <span>💰 {fmtINR(j.salary)}</span>
                      <span>📍 {j.city}</span>
                      <span>🧑‍🤝‍🧑 {j.vacancies} Vacancies</span>
                    </div>
                    <a
                      href={`/agent/jobs/${j.id}/apply`}
                      className="inline-block mt-3 text-[10px] font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 px-3 py-1.5 rounded-full shadow-sm transition"
                    >
                      Apply Candidate
                    </a>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Commission Widget */}
            <SectionCard title="Commission">
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-gray-500">Today's Commission</span>
                  <span className="font-bold text-[#0D1B3D]">{fmtINR(commission.today)}</span>
                </div>
                <div className="flex justify-between items-baseline border-t border-gray-50 pt-3">
                  <span className="text-xs text-gray-500">Monthly</span>
                  <span className="font-bold text-[#0D1B3D]">{fmtINR(commission.monthly)}</span>
                </div>
                <div className="flex justify-between items-baseline border-t border-gray-50 pt-3">
                  <span className="text-xs text-gray-500">Pending</span>
                  <span className="font-bold text-amber-600">{fmtINR(commission.pending)}</span>
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            {/* Assigned Employers */}
            <SectionCard title="Assigned Employers" className="xl:col-span-2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100">
                      <th className="pb-2 font-medium">Company</th>
                      <th className="pb-2 font-medium">Contact Person</th>
                      <th className="pb-2 font-medium">Phone</th>
                      <th className="pb-2 font-medium">Jobs</th>
                      <th className="pb-2 font-medium">Last Contact</th>
                      <th className="pb-2 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employers.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-gray-400 text-sm">
                          No employers assigned yet.
                        </td>
                      </tr>
                    )}
                    {employers.map((e) => (
                      <tr key={e.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                        <td className="py-2.5">{e.company_name}</td>
                        <td className="py-2.5">{e.contact_person}</td>
                        <td className="py-2.5">{e.phone}</td>
                        <td className="py-2.5">{e.jobs_count}</td>
                        <td className="py-2.5">{e.last_contact}</td>
                        <td className="py-2.5 text-right space-x-2 whitespace-nowrap">
                          <a href={`tel:${e.phone}`} className="text-blue-600 hover:underline">Call</a>
                          <a href={`https://wa.me/91${e.phone}`} target="_blank" rel="noreferrer" className="text-green-600 hover:underline">WhatsApp</a>
                         <a href={`/agent/employers/${e.id}`} className="text-gray-500 hover:underline">View</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>


            {/* Employer Subscriptions
<SectionCard title="Employer Subscriptions" className="mb-6">
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100">
          <th className="pb-2 font-medium">ID</th>
          <th className="pb-2 font-medium">Employer</th>
          <th className="pb-2 font-medium">Plan</th>
          <th className="pb-2 font-medium">Amount</th>
          <th className="pb-2 font-medium">Cycle</th>
          <th className="pb-2 font-medium">Status</th>
          <th className="pb-2 font-medium">Expires</th>
        </tr>
      </thead>
      <tbody>
        {subscriptions.length === 0 && (
          <tr>
            <td colSpan={7} className="py-4 text-center text-gray-400 text-sm">
              No subscriptions found for your assigned employers.
            </td>
          </tr>
        )}
        {subscriptions.map((s) => (
          <tr key={s.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
            <td className="py-2.5 text-gray-500">#{s.id}</td>
            <td className="py-2.5 font-medium text-[#0D1B3D]">{s.employer_name}</td>
            <td className="py-2.5 capitalize">{s.plan_name}</td>
            <td className="py-2.5">{fmtINR(s.amount)}</td>
            <td className="py-2.5 capitalize">{s.billing_cycle ?? '—'}</td>
            <td className="py-2.5">
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${
                  s.status === 'active'
                    ? 'bg-green-50 text-green-600'
                    : s.status === 'expired'
                    ? 'bg-gray-100 text-gray-500'
                    : 'bg-red-50 text-red-500'
                }`}
              >
                {s.status}
              </span>
            </td>
            <td className="py-2.5 text-gray-500">{s.expires_at}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</SectionCard> */}

            {/* Notifications */}
            <SectionCard title="Recent Notifications">
              <ul className="space-y-3">
                {notifications.length === 0 && <p className="text-sm text-gray-400 text-center py-4">You're all caught up.</p>}
                {notifications.map((n) => (
                  <li key={n.id} className="flex items-start justify-between gap-2 text-sm">
                    <div>
                      <p className={n.read_at ? 'text-gray-500' : 'text-[#0D1B3D] font-medium'}>{n.message}</p>
                      <p className="text-xs text-gray-400">{n.time_ago}</p>
                    </div>
                    {!n.read_at && (
                      <button className="text-xs font-semibold text-purple-600 hover:underline whitespace-nowrap">Mark Read</button>
                    )}
                  </li>
                ))}
              </ul>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            {/* Charts */}
            <SectionCard title="Candidates Added — Last 7 Days" className="xl:col-span-2">
              <div className="flex items-end gap-3 h-40">
                {weeklyTrend.length === 0 && <p className="text-sm text-gray-400 m-auto">No data yet.</p>}
                {weeklyTrend.map((d) => (
                  <div key={d.label} className="flex-1 flex flex-col items-center justify-end h-full">
                    <div
                      className="w-full bg-purple-100 rounded-t-md relative group"
                      style={{ height: `${(d.count / maxTrend) * 100}%`, minHeight: d.count ? '4px' : '2px' }}
                    >
                      <div className="w-full h-full bg-gradient-to-t from-purple-600 to-pink-500 rounded-t-md opacity-90" />
                      <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs font-medium text-[#0D1B3D]">
                        {d.count}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 mt-2">{d.label}</span>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Recent Activities */}
            <SectionCard title="Recent Activities">
              <ol className="relative border-l border-gray-100 ml-2 space-y-5">
                {activities.length === 0 && <p className="text-sm text-gray-400 pl-4">No activity yet today.</p>}
                {activities.map((a) => (
                  <li key={a.id} className="ml-4">
                    <div className="absolute w-2 h-2 bg-purple-600 rounded-full -left-1 mt-1.5" />
                    <p className="text-xs text-gray-400">{a.time}</p>
                    <p className="text-sm text-gray-700">{a.description}</p>
                  </li>
                ))}
              </ol>
            </SectionCard>
          </div>

                   {status === 'loading' && (
            <p className="text-center text-gray-400 text-sm py-4">Loading dashboard…</p>
          )}
        </>
      )}
    </div>
    </AgentLayout>
  );
}

export default AgentDashboard;